import type { ReactNode } from 'react';
import variables from 'common/styles/variables.json';
import { Card } from '../card';
import { Col } from '../col';
import { Empty } from '../empty';
import { InfinityLoading } from '../infinity-loading';
import { Row } from '../row';
import { Skeleton } from '../skeleton';
import { Text } from '../typography';
import { SliderControls } from './slider-controls';
import type { ControlsProperties } from './slider-controls';

type P = {
  colSpan?: 10 | 7;
  controls?: ControlsProperties;
  description?: ReactNode;
  emptyState?: ReactNode;
  items?: Array<{ children: ReactNode; key: string }>;
  queryData: {
    fetchNextPage: () => void;
    hasNextPage?: boolean;
    isFetching: boolean;
    isLoading: boolean;
  };
};

const Slider = ({
  colSpan = 7,
  controls,
  description,
  emptyState,
  items,
  queryData: { fetchNextPage, hasNextPage, isFetching, isLoading },
}: P) => {
  // TODO: this check should be done outside of slider and slider should render card with items or empty state
  if (items?.length === 0) {
    return null;
  }

  return (
    <Card className="slider">
      <Row>
        <Col span={24}>
          <SliderControls {...controls} />
        </Col>
        {description ? (
          <Col className="slider__description" md={12} span={24}>
            <Text>{description}</Text>
          </Col>
        ) : null}
      </Row>

      {isLoading ? (
        <Skeleton active paragraph={{ rows: 6 }} />
      ) : (
        <>
          {items && items.length > 0 ? (
            <Row
              className="slider__row"
              gutter={['ms', 0]}
              style={{ marginLeft: -variables.spaceMd.value, marginRight: -variables.spaceMd.value }}
              wrap={false}
            >
              {items.map((sliderItem) => (
                <Col key={sliderItem.key} span={colSpan}>
                  {sliderItem.children}
                </Col>
              ))}
              {isFetching || hasNextPage ? (
                <Col>
                  <InfinityLoading
                    className="slider__loading"
                    hasMoreData={hasNextPage}
                    isLoading={isFetching}
                    onEnter={fetchNextPage}
                  />
                </Col>
              ) : null}
            </Row>
          ) : (
            (emptyState ?? <Empty />)
          )}
        </>
      )}
    </Card>
  );
};

export default Slider;
